import { FunctionComponent } from 'react';
import { useNavigate } from 'react-router-dom';
import MedicalServicesIcon from '@mui/icons-material/MedicalServices';
import styles from './ProcedureCard.module.css';

export interface Procedure {
  id: string;
  name: string;
  category?: string;
  priceRange?: {
    min: number;
    max: number;
  };
  averagePrice?: number;
  insuranceCoverage?: string;
  providers?: number;
  description?: string;
}

export interface ProcedureCardProps {
  procedure: Procedure;
  variant?: 'compact' | 'standard' | 'detailed';
  showCategory?: boolean;
  showPriceRange?: boolean;
  showAveragePrice?: boolean;
  showInsuranceCoverage?: boolean;
  showProviderCount?: boolean;
  showDescription?: boolean;
  onClick?: (procedure: Procedure) => void;
  className?: string;
}

const ProcedureCard: FunctionComponent<ProcedureCardProps> = ({
  procedure,
  variant = 'standard',
  showCategory = true,
  showPriceRange = true,
  showAveragePrice = true,
  showInsuranceCoverage = true,
  showProviderCount = true,
  showDescription = false,
  onClick,
  className
}) => {
  const navigate = useNavigate();

  const handleClick = () => {
    if (onClick) {
      onClick(procedure);
    } else {
      navigate(`/procedure/${procedure.id}`);
    }
  };

  const cardClasses = [
    styles.procedureCard,
    styles[variant],
    className
  ].filter(Boolean).join(' ');

  return (
    <div className={cardClasses} onClick={handleClick}>
      <div className={styles.procedureInfo}>
        <div className={styles.procedureHeader}>
          <MedicalServicesIcon className={styles.typeIcon} />
          <div className={styles.procedureDetails}>
            <h3 className={styles.procedureName}>{procedure.name}</h3>
            {showCategory && procedure.category && (
              <p className={styles.procedureCategory}>{procedure.category}</p>
            )}
            {showDescription && procedure.description && (
              <p className={styles.procedureDescription}>{procedure.description}</p>
            )}
          </div>
        </div>
        
        {(showPriceRange || showAveragePrice) && (procedure.priceRange || procedure.averagePrice) && (
          <div className={styles.priceInfo}>
            {showPriceRange && procedure.priceRange && (
              <div className={styles.priceRange}>
                <span className={styles.priceLabel}>Price Range</span>
                <span className={styles.priceValue}>
                  ${procedure.priceRange.min} - ${procedure.priceRange.max}
                </span>
              </div>
            )}
            {showAveragePrice && procedure.averagePrice && (
              <div className={styles.averagePrice}>
                <span className={styles.priceLabel}>Average</span>
                <span className={styles.priceValue}>${procedure.averagePrice}</span>
              </div>
            )}
          </div>
        )}
        
        {(showInsuranceCoverage || showProviderCount) && (procedure.insuranceCoverage || procedure.providers) && (
          <div className={styles.procedureFooter}>
            {showInsuranceCoverage && procedure.insuranceCoverage && (
              <span className={styles.insuranceCoverage}>
                Insurance covers {procedure.insuranceCoverage}
              </span>
            )}
            {showProviderCount && procedure.providers && (
              <span className={styles.providerCount}>
                {procedure.providers} providers nearby
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProcedureCard;